import { memo } from 'react';
import {
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  Stack,
} from '@chakra-ui/react';
import { ChartOrderBy, DisplayConfigQuestionDto } from '@keyops-cep/api-client';

import i18n from '../../utils/i18n';
import {
  KeyOpsPrimaryButton,
  KeyOpsSecondaryButton,
} from '../ButtonComponents';

type EditDisplayConfigPanelProps = {
  label: string;
  setLabel: React.Dispatch<React.SetStateAction<string>>;
  displayConfig: DisplayConfigQuestionDto;
  setDisplayConfig: React.Dispatch<
    React.SetStateAction<DisplayConfigQuestionDto>
  >;
  onClose?: () => void;
  onSave?: () => void;
};

export const EditDisplayConfigPanel = memo(function EditDisplayConfigPanel({
  label,
  setLabel,
  displayConfig,
  setDisplayConfig,
  onClose,
  onSave,
}: EditDisplayConfigPanelProps) {
  return (
    <Flex direction="column" h="100%" p={5}>
      <Flex align="center" justify="start" mb={2}>
        <Heading size="md">{i18n.t('visualization.editPanel.title')}</Heading>
      </Flex>
      <Divider mb={3} />
      <Stack spacing={4} flex="1" minH={0} overflowY="auto">
        <FormControl>
          <FormLabel fontSize="md">
            {i18n.t('visualization.editPanel.label')}
          </FormLabel>
          <Input
            background={'white'}
            size="md"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()} // Prevent arrow key navigation in the expanded view
            placeholder={i18n.t('visualization.editPanel.labelPlaceholder')}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="md">
            {i18n.t('visualization.editPanel.chartTitle')}
          </FormLabel>
          <Input
            background={'white'}
            size="md"
            value={displayConfig.chartTitle ?? ''}
            onChange={(event) =>
              setDisplayConfig((displayConfig) => ({
                ...displayConfig,
                chartTitle: event.target.value,
              }))
            }
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={i18n.t(
              'visualization.editPanel.chartTitlePlaceholder'
            )}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="md">
            {i18n.t('visualization.editPanel.xAxisTitle')}
          </FormLabel>
          <Input
            background={'white'}
            size="md"
            value={displayConfig.xAxisTitle ?? ''}
            onChange={(event) =>
              setDisplayConfig((displayConfig) => ({
                ...displayConfig,
                xAxisTitle: event.target.value,
              }))
            }
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={i18n.t(
              'visualization.editPanel.xAxisTitlePlaceholder'
            )}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="md">
            {i18n.t('visualization.editPanel.yAxisTitle')}
          </FormLabel>
          <Input
            background={'white'}
            size="md"
            value={displayConfig.yAxisTitle ?? ''}
            onChange={(e) =>
              setDisplayConfig((displayConfig) => ({
                ...displayConfig,
                yAxisTitle: e.target.value,
              }))
            }
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={i18n.t(
              'visualization.editPanel.yAxisTitlePlaceholder'
            )}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="md">
            {i18n.t('visualization.editPanel.orderBy')}
          </FormLabel>
          <Select
            background="white"
            size="md"
            value={displayConfig.orderBy ?? ChartOrderBy.INITIAL}
            onChange={(event) =>
              setDisplayConfig((displayConfig) => ({
                ...displayConfig,
                orderBy: event.target.value as ChartOrderBy,
              }))
            }
            onKeyDown={(event) => event.stopPropagation()}
          >
            {Object.values(ChartOrderBy).map((orderKey) => (
              <option key={orderKey} value={orderKey}>
                {orderKey.replace('_', ' ')}
              </option>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <Flex
        align="center"
        justify="center"
        gap={5}
        pt={5}
        background="keyops.whiteSmoke"
      >
        <KeyOpsSecondaryButton onClick={onClose}>
          {i18n.t('common.cancel')}
        </KeyOpsSecondaryButton>
        <KeyOpsPrimaryButton onClick={onSave}>
          {i18n.t('common.update')}
        </KeyOpsPrimaryButton>
      </Flex>
    </Flex>
  );
});
